import { endpoints } from '@api'
import { useToast } from '@chakra-ui/react'
import { CLIENT_PRONOUNS } from '@constants/clientPronouns'
import { formatPhone } from '@utilities'
import moment from 'moment'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import { useMutation, useQueryClient } from 'react-query'
import {
  getClinicianSchedulingControllerGetClinicianScheduleQueryKey,
  useClinicianSchedulingControllerCreateAppointment,
  useClinicianSchedulingControllerDeleteAppointment,
  useClinicianSchedulingControllerUpdateAppointment,
  useServiceTypeControllerFindAll
} from '~/clinician-api'
import type {
  CancelAppointmentDto,
  CreateAppointmentWithNotificationDtoSetting,
  ServiceTypesResponse,
  ServiceTypesResponseItem,
  UpdateAppointmentWithNotificationDtoSetting
} from '~/clinician-api/models'
import type { User } from '../../../store/types'
import type { AddEditEventFormValues } from '../components/event-modals/add-edit-event-modal'
import { EventModalsManager } from '../components/event-modals/event-modals-manager'
import { useCalendarSettings } from '../hooks/use-calendar-settings'
import {
  BpEvent,
  type Client,
  EVENT_SETTING_IN_PERSON,
  EVENT_SETTING_TELEHEALTH,
  type EventSetting,
  EventType,
  NotificationType
} from '../models'
import {
  getRecurringTitle,
  transformRecurrenceToDto
} from '../utils/recurrence.utils'
import { useCalendar } from './calendar.context'

export interface ServiceTypeOption {
  value: string
  label: string
  duration: number
}

export interface RecurringOption {
  value: string
  label: string
}

export interface SettingOption {
  value: EventSetting
  label: string
}

export interface PickerValue {
  client: Client | null
  inputText: string
}

type CalendarEventContextType = {
  user: User
  // Current event being viewed/edited
  currentEvent: BpEvent | null
  setCurrentEvent: (event: BpEvent | null) => void
  serviceTypeOptions: ServiceTypeOption[]
  recurringOptions: RecurringOption[]
  getRecurringTitle: (event: BpEvent) => string | null
  settingOptions: SettingOption[]
  getSettingTitle: (setting: string) => string
  currentPickerValue: PickerValue | null
  setCurrentPickerValue: (value: PickerValue | null) => void

  // Individual field setters for easier form management
  updateEventField: (
    field: keyof BpEvent,
    value: BpEvent[keyof BpEvent]
  ) => void

  // Utility methods
  clearEvent: () => void
  isEditMode: boolean
  setIsEditMode: (isEdit: boolean) => void

  setNotificationType: (notificationType: NotificationType) => void
  notificationType: NotificationType

  // Modal attributes
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  onClose: () => void

  // Modal states
  showClientPickerModal: boolean
  setShowClientPickerModal: (show: boolean) => void
  showCreateClientModal: boolean
  setShowCreateClientModal: (show: boolean) => void
  showAddEditEventModal: boolean
  setShowAddEditEventModal: (show: boolean) => void
  showSendNotificationModal: boolean
  setShowSendNotificationModal: (show: boolean) => void
  showViewEventModal: boolean
  setShowViewEventModal: (show: boolean) => void
  showMissingClientInfoModal: boolean
  setShowMissingClientInfoModal: (show: boolean) => void
  showCancelEventModal: boolean
  setShowCancelEventModal: (show: boolean) => void

  // handle interstitial events
  handleClientPickerSubmit: (value: PickerValue) => void
  handleAddEditEventSubmit: (values: AddEditEventFormValues) => void
  handleSwitchEvent: (eventType: EventType) => void

  // handle crud operations
  handleDeleteEvent: (eventId?: string, sendNotification?: boolean) => void
  handleRecurringCancellation: (data: CancelAppointmentDto) => void
  handleSaveEvent: (event?: BpEvent | null, sendNotification?: boolean) => void
  handleEnrollClient: (
    firstName: string,
    lastName: string,
    pronoun?: string | null,
    email?: string,
    phoneNumber?: string
  ) => void
  handleMissingClientInfoSubmit: (
    patient_id: string,
    email: string,
    phoneNumber: string
  ) => void
  isCreateEventLoading: boolean
  isUpdateEventLoading: boolean
  isDeleteEventLoading: boolean
  handleIntakeDocumentsSent: () => void
  showSendIntakeModal: boolean
  setShowSendIntakeModal: (show: boolean) => void
}

export const CalendarEventContext = createContext<
  CalendarEventContextType | undefined
>(undefined)

export const CalendarEventProvider = ({
  children
}: {
  children: React.ReactNode
}) => {
  const { viewCurrentStart, viewCurrentEnd } = useCalendar()
  const queryClient = useQueryClient()
  const { user } = useCalendar()
  const { enabledCalendarIds } = useCalendarSettings()
  const [currentEvent, setCurrentEvent] = useState<BpEvent | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)

  // Modal states
  const [showClientPickerModal, setShowClientPickerModal] = useState(false)
  const [showCreateClientModal, setShowCreateClientModal] = useState(false)
  const [showAddEditEventModal, setShowAddEditEventModal] = useState(false)
  const [showSendNotificationModal, setShowSendNotificationModal] =
    useState(false)
  const [showViewEventModal, setShowViewEventModal] = useState(false)
  const [showMissingClientInfoModal, setShowMissingClientInfoModal] =
    useState(false)
  const [showCancelEventModal, setShowCancelEventModal] = useState(false)
  const [currentPickerValue, setCurrentPickerValue] =
    useState<PickerValue | null>(null)
  const [showSendIntakeModal, setShowSendIntakeModal] = useState(false)
  const toast = useToast()
  // Modal attributes
  const [isOpen, setIsOpen] = useState(false)
  const [notificationType, setNotificationType] = useState<NotificationType>(
    NotificationType.CONFIRM
  )

  const updateEventField = (
    field: keyof BpEvent,
    value: BpEvent[keyof BpEvent]
  ) => {}

  const { mutateAsync: createAppointment, isLoading: isCreateEventLoading } =
    useClinicianSchedulingControllerCreateAppointment({
      mutation: {
        onSuccess() {
          const queryKey =
            getClinicianSchedulingControllerGetClinicianScheduleQueryKey({
              selectedCalendarSystemIds: enabledCalendarIds,
              startDate: viewCurrentStart?.toISOString() || '',
              endDate: viewCurrentEnd?.toISOString() || ''
            })
          queryClient.invalidateQueries({ queryKey: queryKey })
        }
      }
    })

  const { mutateAsync: updateAppointment, isLoading: isUpdateEventLoading } =
    useClinicianSchedulingControllerUpdateAppointment({
      mutation: {
        onSuccess() {
          const queryKey =
            getClinicianSchedulingControllerGetClinicianScheduleQueryKey({
              selectedCalendarSystemIds: enabledCalendarIds,
              startDate: viewCurrentStart?.toISOString() || '',
              endDate: viewCurrentEnd?.toISOString() || ''
            })
          queryClient.invalidateQueries({ queryKey: queryKey })
        }
      }
    })

  const { mutateAsync: deleteAppointment, isLoading: isDeleteEventLoading } =
    useClinicianSchedulingControllerDeleteAppointment({
      mutation: {
        onSuccess() {
          const queryKey =
            getClinicianSchedulingControllerGetClinicianScheduleQueryKey({
              selectedCalendarSystemIds: enabledCalendarIds,
              startDate: viewCurrentStart?.toISOString() || '',
              endDate: viewCurrentEnd?.toISOString() || ''
            })
          queryClient.invalidateQueries({ queryKey })
          clearEvent()
        }
      }
    })

  const { data: serviceTypeOptions = [] } = useServiceTypeControllerFindAll({
    query: {
      select: (data: ServiceTypesResponse) => {
        return data.map((serviceType: ServiceTypesResponseItem) => {
          return {
            value: serviceType.code,
            label: serviceType.text || '',
            duration: serviceType.duration
          }
        })
      }
    }
  })

  const { mutateAsync: enrollClient } = useMutation(
    endpoints.postSessionEnrollClient.request
  )

  const { mutate: updateClientAccount } = useMutation(
    endpoints.patchClinicianUserInfo.request
  )

  const recurringOptions: RecurringOption[] = useMemo(
    () => [
      { value: 'no-repeat', label: 'Does not repeat' },
      { value: 'daily', label: 'Daily' },
      { value: 'weekly', label: 'Weekly' },
      { value: 'bi-weekly', label: 'Bi-weekly' },
      { value: 'third-week', label: 'Every 3rd week' },
      { value: 'fourth-week', label: 'Every 4th week' }
    ],
    []
  )

  const settingOptions: SettingOption[] = useMemo(
    () => [
      { value: EVENT_SETTING_IN_PERSON, label: 'In person' },
      { value: EVENT_SETTING_TELEHEALTH, label: 'Telehealth' }
    ],
    []
  )

  const clearEvent = useCallback(() => {
    setCurrentEvent(null)
    setIsEditMode(false)
    setShowClientPickerModal(false)
    setShowAddEditEventModal(false)
    setShowSendNotificationModal(false)
    setShowViewEventModal(false)
    setShowMissingClientInfoModal(false)
    setShowCreateClientModal(false)
    setShowCancelEventModal(false)
    setCurrentPickerValue(null)
  }, [])

  const onClose = useCallback(() => {
    setIsOpen(false)
    clearEvent()
  }, [clearEvent])

  const handleClientPickerSubmit = useCallback(
    (value: PickerValue) => {
      setCurrentPickerValue(value)
      let patient: Client | undefined
      let eventType = EventType.OTHER_EVENT
      let subject = value.inputText
      let duration = 30

      if (value.client) {
        patient = value.client
        eventType = EventType.CLIENT_SESSION
        subject = `Appointment with ${value.client.firstName} ${value.client.lastName}`
        duration = serviceTypeOptions?.[0]?.duration || 30
      }

      const event = currentEvent
        ? currentEvent
        : new BpEvent({ clinician_id: user.id })
      setCurrentEvent(
        event.withChanges({
          eventType: eventType,
          patient: patient,
          subject: subject,
          end: moment(event.start).add(duration, 'minutes').toISOString()
        })
      )
      setShowClientPickerModal(false)
      setShowAddEditEventModal(true)
    },
    [currentEvent, serviceTypeOptions, user]
  )

  const handleSaveEvent = useCallback(
    async (event?: BpEvent | null, sendNotification: boolean = false) => {
      if (!event) {
        return
      }

      if (event?.id) {
        await updateAppointment({
          eventId: event.id,
          data: {
            subject: event.subject,
            start: event.start,
            end: event.end,
            patientId: event.patient?.id,
            description: event.notes || '',
            setting:
              event.setting as UpdateAppointmentWithNotificationDtoSetting,
            serviceType: event.serviceType
              ? { code: event.serviceType.code, text: event.serviceType.text }
              : undefined,
            recurrenceTemplate: event.recurrenceTemplate,
            sendNotification
          }
        })
        return
      }

      if (event?.eventType === EventType.OTHER_EVENT) {
        await createAppointment({
          data: {
            subject: event.subject || 'Event',
            start: event.start,
            end: event.end,
            description: event.notes || '',
            setting:
              event.setting as CreateAppointmentWithNotificationDtoSetting,
            recurrenceTemplate: event.recurrenceTemplate,
            sendNotification: false
          }
        })
        return
      }

      await createAppointment({
        data: {
          subject: event?.subject || 'Appointment',
          start: moment(event?.start).toISOString(),
          end: moment(event?.end).toISOString(),
          description: event?.notes || '',
          patientId: event?.patient?.id,
          setting:
            event?.setting as CreateAppointmentWithNotificationDtoSetting,
          serviceType: event.serviceType
            ? {
                code: event.serviceType.code,
                text: event.serviceType.text
              }
            : undefined,
          recurrenceTemplate: event.recurrenceTemplate,
          sendNotification
        }
      })

      return
    },
    [updateAppointment, createAppointment]
  )

  const handleAddEditEventSubmit = useCallback(
    async (values: AddEditEventFormValues) => {
      const dateMoment = moment(values.eventDate)
      const start = moment(
        `${dateMoment.format('YYYY-MM-DD')} ${values.eventStartTime}`,
        'YYYY-MM-DD hh:mm a'
      )
      const end = moment(
        `${dateMoment.format('YYYY-MM-DD')} ${values.eventEndTime}`,
        'YYYY-MM-DD hh:mm a'
      )

      const event = currentEvent
        ? currentEvent
        : new BpEvent({ clinician_id: user.id })

      const recurrenceTemplate = transformRecurrenceToDto({
        recurring: values.recurring,
        occurrenceCount: values.occurrenceCount
      })
      const newEvent = event.withChanges({
        eventType: currentEvent?.eventType || EventType.CLIENT_SESSION,
        serviceType: values.serviceType?.value
          ? { code: values.serviceType.value, text: values.serviceType.label }
          : undefined,
        start: start.toISOString(),
        end: end.toISOString(),
        setting: values.setting,
        recurring: values.recurring || 'no-repeat',
        recurrenceTemplate: recurrenceTemplate,
        notes: values.description || ''
      })
      setCurrentEvent(newEvent)

      // If the event type is an "other event", close the modal when done
      if (currentEvent?.eventType === EventType.OTHER_EVENT) {
        handleSaveEvent(newEvent)
        onClose()
        return
      }

      // Check to see if the client has an email address, if not, show the missing client info modal
      // TODO: Deal with phone number too
      if (currentEvent?.patient?.id && !currentEvent?.patient?.email) {
        setShowAddEditEventModal(false)
        setShowMissingClientInfoModal(true)
        return
      }

      // If updating an existing event, open the send notification modal
      if (currentEvent?.id) {
        setShowAddEditEventModal(false)
        setNotificationType(NotificationType.UPDATE)
        setShowSendNotificationModal(true)
        return
      }

      setShowAddEditEventModal(false)
      setNotificationType(NotificationType.CONFIRM)
      setShowSendNotificationModal(true)
    },
    [currentEvent, user, onClose, handleSaveEvent]
  )

  const handleSwitchEvent = useCallback(
    (eventType: EventType) => {
      if (!currentEvent) {
        return
      }

      setCurrentEvent(
        currentEvent.withChanges({
          eventType: eventType
        })
      )
    },
    [currentEvent]
  )

  const handleEnrollClient = useCallback(
    async (
      firstName: string,
      lastName: string,
      pronoun?: string | null,
      email?: string,
      phoneNumber?: string
    ) => {
      const enrollData = {
        firstName: firstName,
        lastName: lastName,
        email: email,
        phoneNumber: phoneNumber ? formatPhone(phoneNumber) : undefined,
        pronoun: pronoun
          ? CLIENT_PRONOUNS[pronoun as keyof typeof CLIENT_PRONOUNS]
          : null
      }

      const patient = await enrollClient({
        data: enrollData
      })
      const event = currentEvent
        ? currentEvent
        : new BpEvent({ clinician_id: user.id })

      const duration = serviceTypeOptions?.[0]?.duration || 30

      const changeEvent = event.withChanges({
        eventType: EventType.CLIENT_SESSION,
        patient: { id: patient.patientId, firstName, lastName, email },
        subject: `Appointment with ${firstName} ${lastName}`,
        end: moment(event.start).add(duration, 'minutes').toISOString(),
        isNewClient: true
      })
      setCurrentEvent(changeEvent)
      setShowCreateClientModal(false)
      setShowAddEditEventModal(true)
    },
    [enrollClient, currentEvent, user, serviceTypeOptions]
  )

  const handleMissingClientInfoSubmit = useCallback(
    (patient_id: string, email: string, phoneNumber: string) => {
      updateClientAccount({
        patientId: patient_id,
        data: {
          email: email,
          phone_number: phoneNumber
        }
      })

      setShowMissingClientInfoModal(false)
      setShowSendNotificationModal(true)
    },
    [updateClientAccount]
  )

  const handleDeleteEvent = useCallback(
    (eventId?: string, sendNotification: boolean = false) => {
      if (eventId) {
        deleteAppointment({ eventId: eventId, data: { sendNotification } })
      }
      onClose()
    },
    [deleteAppointment, onClose]
  )

  const handleRecurringCancellation = useCallback(
    async (data: CancelAppointmentDto) => {
      if (currentEvent?.id) {
        try {
          await deleteAppointment({ eventId: currentEvent.id, data })

          toast({
            title: 'Appointment cancelled',
            description: `Successfully cancelled ${data.cancellationMode === 'single' ? 'this appointment' : 'this and all future appointments'}.`,
            status: 'success',
            duration: 5000,
            isClosable: true
          })
        } catch (error) {
          toast({
            title: 'Error',
            description: 'Failed to cancel appointment. Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true
          })
        }
      }
      onClose()
    },
    [currentEvent?.id, deleteAppointment, onClose, toast]
  )

  const getSettingTitle = (setting: string) => {
    return settingOptions.find(option => option.value === setting)?.label || ''
  }

  const handleIntakeDocumentsSent = () => {
    setShowSendIntakeModal(false)
  }

  return (
    <CalendarEventContext.Provider
      value={{
        user,
        currentEvent,
        setCurrentEvent,
        updateEventField,
        clearEvent,
        isEditMode,
        setIsEditMode,
        isOpen,
        setIsOpen,
        onClose,
        showClientPickerModal,
        setShowClientPickerModal,
        showCreateClientModal,
        setShowCreateClientModal,
        showAddEditEventModal,
        setShowAddEditEventModal,
        showSendNotificationModal,
        setShowSendNotificationModal,
        showViewEventModal,
        setShowViewEventModal,
        showMissingClientInfoModal,
        setShowMissingClientInfoModal,
        showCancelEventModal,
        setShowCancelEventModal,
        currentPickerValue,
        setCurrentPickerValue,
        handleClientPickerSubmit,
        handleAddEditEventSubmit,
        handleSwitchEvent,
        handleDeleteEvent,
        handleRecurringCancellation,
        handleSaveEvent,
        handleEnrollClient,
        handleIntakeDocumentsSent,
        isCreateEventLoading,
        isUpdateEventLoading,
        isDeleteEventLoading,
        serviceTypeOptions,
        recurringOptions,
        getRecurringTitle,
        settingOptions,
        getSettingTitle,
        showSendIntakeModal,
        setShowSendIntakeModal,
        handleMissingClientInfoSubmit,
        notificationType,
        setNotificationType
      }}
    >
      <EventModalsManager />
      {children}
    </CalendarEventContext.Provider>
  )
}

export const useCalendarEvent = () => {
  const context = useContext(CalendarEventContext)
  if (!context) {
    throw new Error(
      'useCalendarEvent must be used within a CalendarEventProvider'
    )
  }
  return context
}
